/**
 * Organization Members — team management with invite.
 */
import { useState, useCallback, useMemo } from 'react'
import { View, Text } from 'react-native'
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { notification, selection, NotificationType } from '@/lib/utils/haptics'
import Animated from 'react-native-reanimated'
import { UserPlus, Mail, Phone } from '@/components/ui/icons'
import { Button } from '@/components/ui/Button'
import { Pressable } from '@/components/ui/Pressable'
import { TextInput } from '@/components/ui/TextInput'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { Badge } from '@/components/ui/Badge'
import { OrgMemberCard } from '@/components/hosting/OrgMemberCard'
import {
  useMyOrganizations,
  useOrgMembers,
  useOrgMembership,
  useOrgPendingInvites,
  useInviteMember,
  useRemoveMember,
} from '@/lib/hooks/use-organization'
import { useAuth } from '@/lib/hooks/use-auth'
import { inviteMemberSchema } from '@/lib/validations/organization.schema'
import { useSemanticColors, SPACING, RADIUS, INTERACTION } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { useTheme } from '@/lib/hooks/use-theme'
import { useEntrance } from '@/lib/hooks/use-entrance'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'

export default function OrgMembersScreen() {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { user } = useAuth()
  const router = useRouter()
  const { orgId: orgIdParam } = useLocalSearchParams<{ orgId: string }>()
  /*
   * SIN `?orgId=` ESTA PANTALLA OFRECIA INVITAR Y EL BOTON NO HACIA NADA.
   *
   * Con el parametro ausente todas las consultas quedan `enabled: false`, y en
   * TanStack Query v5 eso deja `isLoading` en FALSE --no true--, porque
   * `isLoading = isPending && isFetching`. Entonces `members` era `[]` con
   * `isLoading` false, la rama de rescate del roster vacio encendia el boton de
   * invitar, el formulario validaba el correo, y al tocar "Invitar"
   * `handleInvite` hacia `if (!orgId) return`: sin mutacion, sin toast, sin
   * error. Nada.
   *
   * Se resuelve igual que en `settings.tsx`: el parametro manda, y si no hay,
   * se usa la primera organizacion de la persona.
   */
  const { organizations, isLoading: isResolvingOrgs } = useMyOrganizations()
  const orgId = orgIdParam || organizations[0]?.id

  const { resolved: resolvedMembership, isMember, isManager } = useOrgMembership(orgId)
  const { members, isLoading } = useOrgMembers(orgId)
  const enter = useEntrance(!isLoading)
  const { pendingInvites } = useOrgPendingInvites(orgId)
  const inviteMember = useInviteMember()
  const removeMember = useRemoveMember()

  const [showInvite, setShowInvite] = useState(false)
  const [inviteMethod, setInviteMethod] = useState<'email' | 'phone'>('email')
  const [inviteEmail, setInviteEmail] = useState('')
  const [invitePhone, setInvitePhone] = useState('')
  const [inviteErrors, setInviteErrors] = useState<Record<string, string>>({})

  /*
   * EL PERMISO SE LE PREGUNTA AL SERVIDOR, NO SE DEDUCE DEL ROSTER.
   *
   * Antes `isManager` salia de `members.some(...)`, y eso es circular: el roster
   * es justo lo que un no-miembro NO puede ver. Ahi RLS no devuelve 403 --la
   * politica `org_members_select_member` FILTRA-- asi que un extrano recibia
   * `[]` sin error, y la rama de rescate `members.length === 0` se encendia para
   * el. Esa rama existia por una razon legitima --una organizacion recien creada
   * cuyo roster todavia esta vacio-- pero no sabia distinguir "vacio porque es
   * nueva" de "vacio porque no es tuya", y le regalaba a cualquiera el boton de
   * invitar y su formulario, con la validacion funcionando, contra un servidor
   * que iba a decir 42501.
   *
   * `is_org_member` / `is_org_manager` son las MISMAS funciones que evaluan las
   * politicas de RLS, asi que esto no agrega una cuarta derivacion del permiso:
   * le pregunta temprano a quien ya decide. Y ninguna lanza excepcion.
   *
   * FALLA ABIERTO: mientras `resolvedMembership` sea false, se conserva la
   * conducta vieja --incluida la rama del roster vacio-- para no ponerle un muro
   * a un administrador legitimo con la red lenta.
   */
  const canManageInvites = resolvedMembership ? isManager : !isLoading && members.length === 0
  const invitePayload = useMemo(
    () => ({
      email: inviteMethod === 'email' ? inviteEmail.trim() || undefined : undefined,
      phone: inviteMethod === 'phone' ? invitePhone.trim() || undefined : undefined,
      role: 'fleet_driver' as const,
    }),
    [inviteEmail, inviteMethod, invitePhone],
  )
  const canInvite = inviteMemberSchema.safeParse(invitePayload).success && !inviteMember.isPending

  const handleInvite = useCallback(async () => {
    if (!orgId) return

    const result = inviteMemberSchema.safeParse({
      email: invitePayload.email,
      phone: invitePayload.phone,
      role: invitePayload.role,
    })

    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        const field = (issue.path[0] as string) ?? 'email'
        fieldErrors[field] = t(issue.message)
      }
      setInviteErrors(fieldErrors)
      void notification(NotificationType.Error)
      return
    }

    try {
      await inviteMember.mutateAsync({
        organizationId: orgId,
        email: result.data.email,
        phone: result.data.phone,
        role: 'fleet_driver',
      })
      void notification(NotificationType.Success)
      toast.success(t('org.invite_sent'))
      setShowInvite(false)
      setInviteEmail('')
      setInvitePhone('')
      setInviteErrors({})
    } catch (err) {
      if (__DEV__) console.error('[OrgMembers] invite', err)
      void notification(NotificationType.Error)
      toast.error(errorMessage(err, t('errors.generic')))
      captureError(err, { screen: 'org_members', action: 'invite' }, { orgId })
    }
  }, [orgId, invitePayload, inviteMember, t])

  const handleRemove = useCallback(
    async (memberId: string) => {
      if (!orgId) return
      try {
        await removeMember.mutateAsync({ memberId, orgId })
        void notification(NotificationType.Success)
        toast.success(t('org.member_removed'))
      } catch (err) {
        if (__DEV__) console.error('[OrgMembers] remove', err)
        toast.error(errorMessage(err, t('errors.generic')))
        captureError(err, { screen: 'org_members', action: 'remove' }, { orgId, memberId })
      }
    },
    [orgId, removeMember, t],
  )

  const toggleInvite = useCallback(() => {
    void selection()
    setShowInvite((open) => !open)
  }, [])

  // Sin ninguna organizacion, esto no es un estado de carga: es la pantalla
  // equivocada. El indice de organizacion es el que sabe crear o unirse a una.
  // Mismo desvio que `settings.tsx`.
  if (!isResolvingOrgs && !orgId) {
    return <Redirect href="/(tabs)/hosting/organization" />
  }

  return (
    <ScreenContainer variant="content" scroll={false}>
      {/* One stack header, laid out like every other screen: back button and
          title on the same row, invite action on the right. This screen used to
          stack a bare back circle over a separate 22px title, which read as a
          second H1 and made a fourth distinct header layout in the app. */}
      <ScreenHeader
        title={t('org.members')}
        backTestID="org.members-back"
        right={
          canManageInvites ? (
            // Icon-only, exactly like the "+" on hosting/listings: a labelled
            // secondary button here is ~160pt wide and would squeeze
            // "Miembros del equipo" into an ellipsis on a 375pt screen. The
            // written-out CTA lives in the empty state, where there is room.
            <Pressable
              onPress={toggleInvite}
              accessibilityRole="button"
              accessibilityLabel={t('org.invite_member')}
              accessibilityState={{ expanded: showInvite }}
              testID="org.invite-button"
              style={{
                width: INTERACTION.minTouchTarget,
                height: INTERACTION.minTouchTarget,
                borderRadius: INTERACTION.minTouchTarget / 2,
                backgroundColor: COLORS.coral.dark,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <UserPlus size={20} color={COLORS.white} strokeWidth={2.2} />
            </Pressable>
          ) : undefined
        }
      />

      <KeyboardAwareView
        includeTopInset={false}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: SPACING.lg,
          paddingBottom: SPACING['3xl'] + SPACING['2xl'],
        }}
      >
        {/* Invite form */}
        {showInvite && (
          <Animated.View
            collapsable={false}
            entering={enter()}
            style={{
              backgroundColor: colors.surface,
              borderRadius: RADIUS.lg,
              padding: SPACING.md,
              marginBottom: SPACING.lg,
              gap: SPACING.sm,
              ...WEB_SHADOWS.sm,
            }}
          >
            <Text style={{ fontSize: 15, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
              {t('org.invite_member')}
            </Text>

            <SegmentedControl
              options={[
                { value: 'email', label: t('org.invite_method_email') },
                { value: 'phone', label: t('org.invite_method_phone') },
              ]}
              value={inviteMethod}
              onChange={(value) => {
                setInviteMethod(value)
                setInviteErrors({})
              }}
              testIDPrefix="org.invite-method"
            />

            {inviteMethod === 'email' ? (
              <TextInput
                label={t('org.invite_by_email')}
                value={inviteEmail}
                onChangeText={(v) => {
                  setInviteEmail(v)
                  setInviteErrors({})
                }}
                error={inviteErrors.email}
                keyboardType="email-address"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                leftIcon={<Mail size={18} color={colors.textMuted} strokeWidth={1.5} />}
                testID="org.invite-email"
              />
            ) : (
              <TextInput
                label={t('org.invite_by_phone')}
                value={invitePhone}
                onChangeText={(v) => {
                  setInvitePhone(v)
                  setInviteErrors({})
                }}
                error={inviteErrors.phone}
                keyboardType="phone-pad"
                inputMode="tel"
                autoComplete="tel"
                leftIcon={<Phone size={18} color={colors.textMuted} strokeWidth={1.5} />}
                testID="org.invite-phone"
              />
            )}

            <Button
              title={t('org.invite_member')}
              onPress={handleInvite}
              loading={inviteMember.isPending}
              disabled={!canInvite}
              variant="primary"
              size="md"
              testID="org.invite-submit"
            />
          </Animated.View>
        )}

        {/* Member list */}
        {isLoading ? (
          <View style={{ gap: SPACING.sm }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} width="100%" height={72} borderRadius={RADIUS.lg} />
            ))}
          </View>
        ) : resolvedMembership && !isMember ? (
          /*
            NO SOS MIEMBRO. Antes esta rama no existia y el no-miembro caia en
            "roster vacio", que le ofrecia invitar gente a una organizacion
            ajena. Decir lo que pasa y ofrecer a donde ir es el patron que este
            repo ya usa en ReadyToBook y en payouts/onboarding.
          */
          <View style={{ flex: 1, justifyContent: 'center' }}>
            <EmptyState
              testID="org.members-not-member"
              variant="bloom"
              illustration={<UserPlus size={36} color={COLORS.coral.DEFAULT} strokeWidth={1.6} />}
              title={t('org.not_member_title')}
              description={t('org.not_member_body')}
              action={{
                label: t('org.not_member_action'),
                onPress: () => router.replace('/(tabs)/hosting/organization' as never),
              }}
            />
          </View>
        ) : members.length === 0 ? (
          // Was an icon tile and one bold line, with no description and no way
          // to add anyone — a screen you could only leave by going back. It now
          // reads like its sibling on /hosting/organization, which is the state
          // this one was measured against: same branded tile at the same scale,
          // a heading, a description that says who a member is, and the invite
          // action the screen exists for, centred in the free region.
          <View style={{ flex: 1, justifyContent: 'center' }} testID="org.members-empty">
            <EmptyState
              variant="bloom"
              illustration={<UserPlus size={36} color={COLORS.coral.DEFAULT} strokeWidth={1.6} />}
              title={t('org.no_members')}
              description={t('org.no_members_desc')}
              action={
                canManageInvites && !showInvite
                  ? { label: t('org.invite_member'), onPress: toggleInvite }
                  : undefined
              }
            />
          </View>
        ) : (
          <View style={{ gap: SPACING.sm }}>
            {members.map((member, index) => (
              <Animated.View key={member.id} collapsable={false} entering={enter(index + 2)}>
                <OrgMemberCard
                  member={member}
                  isCurrentUser={member.profileId === user?.id}
                  canManage={isManager}
                  onRemove={handleRemove}
                />
              </Animated.View>
            ))}
          </View>
        )}

        {!isLoading && pendingInvites.length > 0 && (
          <Animated.View collapsable={false} entering={enter(4)} style={{ marginTop: SPACING.xl }}>
            <Text
              style={{
                fontSize: 14,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
                marginBottom: SPACING.sm,
              }}
            >
              {t('org.pending_invites')}
            </Text>
            <View style={{ gap: SPACING.sm }}>
              {pendingInvites.map((invite) => {
                const label = invite.email ?? invite.phone ?? t('org.invite_pending_external')
                return (
                  <View
                    key={invite.id}
                    testID={`org.pending-invite-${invite.id}`}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      padding: SPACING.md,
                      borderRadius: RADIUS.lg,
                      backgroundColor: colors.surface,
                      borderWidth: 1,
                      borderColor: colors.border,
                      gap: SPACING.md,
                      ...WEB_SHADOWS.sm,
                    }}
                  >
                    <View
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: RADIUS.md,
                        backgroundColor: colors.surfaceSubtle,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {invite.email ? (
                        <Mail size={20} color={colors.textSecondary} strokeWidth={1.5} />
                      ) : (
                        <Phone size={20} color={colors.textSecondary} strokeWidth={1.5} />
                      )}
                    </View>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        style={{
                          fontSize: 14,
                          fontFamily: FONTS.bodyBold,
                          color: colors.textPrimary,
                        }}
                        numberOfLines={1}
                      >
                        {label}
                      </Text>
                      <Text
                        style={{
                          fontSize: 12,
                          lineHeight: 18,
                          fontFamily: FONTS.body,
                          color: colors.textSecondary,
                          marginTop: 2,
                        }}
                        numberOfLines={2}
                      >
                        {t('org.invite_pending_desc')}
                      </Text>
                    </View>
                    <Badge label={t('common.pending')} variant="warning" size="sm" />
                  </View>
                )
              })}
            </View>
          </Animated.View>
        )}
      </KeyboardAwareView>
    </ScreenContainer>
  )
}
